import React, { useState, useRef, useEffect, useCallback, MutableRefObject } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { VoiceContainer, VoiceButton, VoiceSpinner, VoiceDisclosure } from './styles';

// API Configuration from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pogo79jcg8.execute-api.us-east-1.amazonaws.com/prod/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';
const MAX_TEXT_LENGTH = 3000;

type VoiceState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';
type VoiceError = 'generic' | 'too_long' | 'too_many' | 'quota' | null;

export interface VoicePlaybackButtonProps {
  messageId: string;
  text: string;
  audioCacheRef: MutableRefObject<Map<string, string>>;
  autoPlay?: boolean;
  showDisclosure?: boolean;
  isDarkMode: boolean;
  onAutoPlayDone?: () => void;
}

const stripMarkdown = (text: string): string =>
  text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/\*(.+?)\*/gs, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const track = (event: string, props?: Record<string, unknown>) => {
  console.debug('[voice]', event, props);
};

const VoicePlaybackButton: React.FC<VoicePlaybackButtonProps> = ({
  messageId,
  text,
  audioCacheRef,
  autoPlay = false,
  showDisclosure = false,
  isDarkMode,
  onAutoPlayDone,
}) => {
  const { t, language } = useLanguage();
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voiceError, setVoiceError] = useState<VoiceError>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasAutoPlayedRef = useRef(false);

  const cleanText = stripMarkdown(text);
  const meta = { messageId, textLength: cleanText.length, source: 'career_chatbot', language };

  const handleEnded = useCallback(() => {
    setVoiceState('ended');
    track('voice_audio_completed', { messageId, source: 'career_chatbot', language });
  }, [messageId, language]);

  const playFromUrl = useCallback(
    async (url: string) => {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = handleEnded;
      setVoiceState('playing');
      track('voice_audio_started', meta);
      try {
        await audio.play();
      } catch (err) {
        // Browser autoplay policy blocks play() before user interaction — fall back to idle
        // so the cached audio is ready to play on click without another API call.
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setVoiceState('idle');
        } else {
          setVoiceState('error');
          setVoiceError('generic');
        }
      }
    },
    // meta changes identity every render but values are stable — using individual stable deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleEnded, messageId, cleanText.length, language]
  );

  const fetchAndPlay = useCallback(async () => {
    if (cleanText.length > MAX_TEXT_LENGTH) {
      setVoiceState('error');
      setVoiceError('too_long');
      return;
    }

    const cached = audioCacheRef.current.get(messageId);
    if (cached) {
      await playFromUrl(cached);
      return;
    }

    setVoiceState('loading');
    track('voice_audio_requested', meta);

    try {
      const res = await fetch(`${API_BASE_URL}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ messageId, text: cleanText }),
      });

      if (res.status === 413) {
        setVoiceState('error');
        setVoiceError('too_long');
        track('voice_audio_failed', { ...meta, reason: '413' });
        return;
      }
      if (res.status === 429) {
        setVoiceState('error');
        setVoiceError('too_many');
        track('voice_audio_failed', { ...meta, reason: '429' });
        return;
      }
      if (res.status === 503) {
        setVoiceState('error');
        setVoiceError('quota');
        track('voice_audio_failed', { ...meta, reason: '503' });
        return;
      }
      if (!res.ok) throw new Error(`TTS ${res.status}`);

      // AWS API Gateway may return base64-encoded audio even with Content-Type: audio/mpeg
      // when binary media types are not configured on the API. Don't rely on the header —
      // inspect the bytes instead. Real MP3 binary always contains bytes outside the base64
      // alphabet (0xFF MPEG sync, or 0x04/0x00 in ID3 version/flags bytes). If every byte
      // in the first 512 bytes is a valid base64 character, the body is base64 text.
      const buf = await res.arrayBuffer();
      const raw = new Uint8Array(buf);
      const probe = raw.subarray(0, Math.min(raw.length, 512));
      const isBase64 = probe.every(
        b =>
          (b >= 0x41 && b <= 0x5A) || // A-Z
          (b >= 0x61 && b <= 0x7A) || // a-z
          (b >= 0x30 && b <= 0x39) || // 0-9
          b === 0x2B || b === 0x2F || b === 0x3D || // + / =
          b === 0x0A || b === 0x0D    // newlines (MIME line-wrapped base64)
      );

      // Pass ArrayBuffer (not Uint8Array) to Blob to satisfy TypeScript 5's stricter
      // Uint8Array<ArrayBufferLike> vs BlobPart typing.
      let audioBuf: ArrayBuffer;
      if (isBase64) {
        const b64 = new TextDecoder().decode(raw).replace(/\s/g, '');
        const bin = atob(b64);
        audioBuf = new ArrayBuffer(bin.length);
        const view = new Uint8Array(audioBuf);
        for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
      } else {
        audioBuf = buf;
      }

      const blob = new Blob([audioBuf], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      audioCacheRef.current.set(messageId, url);
      await playFromUrl(url);
    } catch (err) {
      console.error('TTS error:', err);
      setVoiceState('error');
      setVoiceError('generic');
      track('voice_audio_failed', { ...meta, reason: String(err) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, cleanText, audioCacheRef, playFromUrl]);

  useEffect(() => {
    if (autoPlay && !hasAutoPlayedRef.current) {
      hasAutoPlayedRef.current = true;
      fetchAndPlay().finally(() => onAutoPlayDone?.());
    }
  }, [autoPlay, fetchAndPlay, onAutoPlayDone]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current = null;
      }
    };
  }, []);

  const handleClick = () => {
    track('voice_play_clicked', meta);

    if (voiceState === 'playing') {
      audioRef.current?.pause();
      setVoiceState('paused');
      track('voice_audio_paused', meta);
      return;
    }
    if (voiceState === 'paused') {
      audioRef.current?.play().catch(() => { setVoiceState('error'); setVoiceError('generic'); });
      setVoiceState('playing');
      return;
    }
    if (voiceState === 'ended') {
      const cached = audioCacheRef.current.get(messageId);
      if (cached) { playFromUrl(cached); return; }
    }
    if (voiceState === 'idle' || voiceState === 'error') {
      setVoiceError(null);
      fetchAndPlay();
    }
  };

  const getLabel = (): string => {
    switch (voiceState) {
      case 'loading': return t('widget.voice.loading');
      case 'playing': return t('widget.voice.pause');
      case 'paused': return t('widget.voice.resume');
      case 'ended': return t('widget.voice.replay');
      case 'error':
        if (voiceError === 'too_long') return t('widget.voice.tooLong');
        if (voiceError === 'too_many') return t('widget.voice.tooMany');
        if (voiceError === 'quota') return t('widget.voice.quotaExceeded');
        return t('widget.voice.error');
      default: return t('widget.voice.play');
    }
  };

  const getAriaLabel = (): string => {
    switch (voiceState) {
      case 'playing': return t('widget.voice.aria.pause');
      case 'paused': return t('widget.voice.aria.resume');
      case 'ended': return t('widget.voice.aria.replay');
      default: return t('widget.voice.aria.play');
    }
  };

  const isDisabled = voiceState === 'loading';
  const isErrorState = voiceState === 'error';

  return (
    <VoiceContainer>
      <VoiceButton
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={getAriaLabel()}
        isDarkMode={isDarkMode}
        isError={isErrorState}
        voiceState={voiceState}
      >
        {voiceState === 'loading' && <VoiceSpinner />}
        {getLabel()}
      </VoiceButton>
      {showDisclosure && (
        <VoiceDisclosure isDarkMode={isDarkMode}>
          {t('widget.voice.disclosure')}
        </VoiceDisclosure>
      )}
    </VoiceContainer>
  );
};

export default VoicePlaybackButton;
